import { PostgrestError } from "@supabase/supabase-js";
import { Notification } from "providers/NotifierProvider";
import { supabase } from "supabase/client";
import { Database } from "supabase/types";
import logSentryError from "utils/logSentryError";

type DbMoneyManager = Database["public"]["Tables"]["money_manager"];
export type MoneyManagerRow = DbMoneyManager["Row"];
export type MoneyManagerInsert = DbMoneyManager["Insert"];
export type MoneyManagerUpdate = DbMoneyManager["Update"];

export const MONEY_MANAGER_ACTIONS = {
  ACTIVATE: "ACTIVATE",
  CANCEL: "CANCEL",
} as const;

export type MoneyManagerAction = keyof typeof MONEY_MANAGER_ACTIONS;

// ── Domain types stored in the JSONB `data` column ────────────────────────

export interface MoneyManagerSubcategory {
  id: string;
  name: string;
  cost: string;
}

export interface MoneyManagerCategory {
  id: string;
  name: string;
  /** per_unit: each person enters how many units they consumed; addon: binary yes/no opt-in */
  type: "per_unit" | "addon";
  /** total cost for this category (auto-computed from subcategories when present) */
  total: string;
  subcategories: MoneyManagerSubcategory[];
}

export interface MoneyManagerParticipantEntry {
  /** For per_unit categories: number of units as a string. For addon: boolean. */
  categories: Record<string, string | boolean>;
  advance: string;
  deduction: string;
  paid: boolean;
}

export interface MoneyManagerData {
  currency: string;
  categories: MoneyManagerCategory[];
  /** keyed by profile id (as string) */
  entries: Record<string, MoneyManagerParticipantEntry>;
}

export const EMPTY_MONEY_MANAGER_DATA: MoneyManagerData = {
  currency: "EUR",
  categories: [],
  entries: {},
};

export interface GetMoneyManagerDataRes {
  data: MoneyManagerData;
}

// ── API class ─────────────────────────────────────────────────────────────

class MoneyManagerApi {
  showNotification?: (notification: Notification) => void;

  constructor(showNotification?: (notification: Notification) => void) {
    this.showNotification = showNotification;
  }

  async activateMoneyManager(eventId: number, groupId: number): Promise<{ error: PostgrestError | null }> {
    const { error } = await supabase.functions.invoke("money-manager", {
      body: JSON.stringify({ eventId, groupId, action: MONEY_MANAGER_ACTIONS.ACTIVATE }),
    });

    if (error) {
      if (this.showNotification) {
        this.showNotification({ message: "Unable to activate Money Manager.", severity: "error" });
      }
      logSentryError(error, "MONEY_MANAGER_API: activateMoneyManager");
      return { error };
    }

    return { error: null };
  }

  async cancelMoneyManager(eventId: number, groupId: number): Promise<{ error: PostgrestError | null }> {
    const { error } = await supabase.functions.invoke("money-manager", {
      body: JSON.stringify({ eventId, groupId, action: MONEY_MANAGER_ACTIONS.CANCEL }),
    });

    if (error) {
      if (this.showNotification) {
        this.showNotification({ message: "Unable to cancel Money Manager.", severity: "error" });
      }
      logSentryError(error, "MONEY_MANAGER_API: cancelMoneyManager");
      return { error };
    }

    return { error: null };
  }

  async getMoneyManagerData(
    eventId: number,
    groupId: number
  ): Promise<{ data: GetMoneyManagerDataRes | null; error: PostgrestError | null }> {
    const { data, error } = await supabase
      .from("money_manager")
      .select("data")
      .eq("event_id", eventId)
      .eq("group_id", groupId)
      .maybeSingle();

    if (error) {
      if (this.showNotification) {
        this.showNotification({ message: "Unable to load Money Manager data.", severity: "error" });
      }
      logSentryError(error, "MONEY_MANAGER_API: getMoneyManagerData");
      return { data: null, error };
    }

    if (!data) {
      return { data: { data: EMPTY_MONEY_MANAGER_DATA }, error: null };
    }

    return { data: { data: (data.data as unknown as MoneyManagerData) ?? EMPTY_MONEY_MANAGER_DATA }, error: null };
  }

  async saveMoneyManagerData(
    eventId: number,
    groupId: number,
    moneyManagerData: MoneyManagerData
  ): Promise<{ error: PostgrestError | null }> {
    const { error } = await supabase
      .from("money_manager")
      .update({ data: moneyManagerData as any })
      .eq("event_id", eventId)
      .eq("group_id", groupId);

    if (error) {
      if (this.showNotification) {
        this.showNotification({ message: "Unable to save Money Manager data.", severity: "error" });
      }
      logSentryError(error, "MONEY_MANAGER_API: saveMoneyManagerData");
      return { error };
    }

    return { error: null };
  }
}

export default MoneyManagerApi;
